import { X } from 'lucide-react'
import { Dialog as DialogPrimitive } from 'radix-ui'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/utils/cn'

interface DialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: ReactNode
  description?: ReactNode
  icon?: ReactNode
  children?: ReactNode
  footer?: ReactNode
  size?: 'sm' | 'md' | 'lg' | 'xl'
  /** Prevent closing on overlay click / Escape (e.g. while submitting). */
  dismissible?: boolean
}

const widths = { sm: 'sm:max-w-md', md: 'sm:max-w-lg', lg: 'sm:max-w-2xl', xl: 'sm:max-w-4xl' }

/**
 * Accessible modal (focus trap, Escape, aria-labelledby). Slides up as a sheet on phones and
 * centres on larger screens.
 */
export function Dialog({
  open,
  onOpenChange,
  title,
  description,
  icon,
  children,
  footer,
  size = 'md',
  dismissible = true,
}: DialogProps) {
  const { t } = useTranslation()
  return (
    <DialogPrimitive.Root
      open={open}
      onOpenChange={(next) => (dismissible || next ? onOpenChange(next) : undefined)}
    >
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 animate-fade-in bg-overlay backdrop-blur-[2px]" />
        <DialogPrimitive.Content
          className={cn(
            'fixed inset-x-0 bottom-0 z-50 flex max-h-[92dvh] w-full animate-slide-up flex-col rounded-t-panel border border-line bg-surface shadow-overlay focus:outline-none sm:inset-auto sm:top-1/2 sm:left-1/2 sm:max-h-[88dvh] sm:-translate-x-1/2 sm:-translate-y-1/2 sm:animate-scale-in sm:rounded-panel',
            widths[size],
          )}
          onEscapeKeyDown={(e) => (dismissible ? undefined : e.preventDefault())}
          onPointerDownOutside={(e) => (dismissible ? undefined : e.preventDefault())}
        >
          <div className="flex items-start gap-3 px-6 pt-5 pb-4">
            {icon ? (
              <span className="grid size-10 shrink-0 place-items-center rounded-full bg-brand-50 text-brand-600 [&_svg]:size-5">
                {icon}
              </span>
            ) : null}
            <div className="min-w-0 flex-1">
              <DialogPrimitive.Title className="text-base font-semibold text-ink">
                {title}
              </DialogPrimitive.Title>
              {description ? (
                <DialogPrimitive.Description className="mt-1 text-sm text-muted">
                  {description}
                </DialogPrimitive.Description>
              ) : (
                <DialogPrimitive.Description className="sr-only">
                  {typeof title === 'string' ? title : t('Dialog')}
                </DialogPrimitive.Description>
              )}
            </div>
            <DialogPrimitive.Close
              className="-mt-1 -mr-2 grid size-8 place-items-center rounded-control text-subtle transition-colors hover:bg-surface-sunken hover:text-ink"
              aria-label={t('Close')}
              disabled={!dismissible}
            >
              <X className="size-4" />
            </DialogPrimitive.Close>
          </div>
          {children ? (
            <div className="min-h-0 flex-1 scrollbar-thin overflow-y-auto px-6 pb-5">{children}</div>
          ) : null}
          {footer ? (
            <div className="flex flex-col-reverse gap-2 border-t border-line bg-surface-muted/60 px-6 py-4 sm:flex-row sm:justify-end sm:rounded-b-panel">
              {footer}
            </div>
          ) : null}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}
